import { useMemo } from 'react';
import { Grid } from '@mui/material';
import { OfficialData } from '../../../networkRequest/officialsService';
import { useInfo } from '@hooks/useInfo';
import Utils from '../../../utils/utils';
import DetailsSection, { DetailRow, EM_DASH } from './DetailsSection';

interface UserDetailViewProps {
  official: OfficialData;
}

const UserDetailsView = ({ official }: UserDetailViewProps) => {
  const { employmentTypeDisplayName } = useInfo();
  const userFields: { [key: string]: DetailRow } = useMemo(() => ({
    organization: {
      label: 'Organization',
      value: official.agency.name,
    },
    title: {
      label: 'Title',
      value: official.title ?? EM_DASH,
    },
    rank: {
      label: 'Rank',
      value: official.rank ?? EM_DASH,
    },
    badge: {
      label: 'Badge Number',
      value: official.badge_number ?? EM_DASH,
    },
    email: {
      label: 'Email',
      value: official.email ?? EM_DASH,
    },
    phone: {
      label: 'Phone',
      value: official.phone_number ?? EM_DASH,
    },
    armed: {
      label: 'Armed',
      value: official.armed_permission ? 'Yes' : 'No',
    },
    designation: {
      label: 'Designation',
      value: employmentTypeDisplayName(official.employment_type) ?? EM_DASH,
    },
    role: {
      label: 'Role',
      value: official.role?.name || 'N/A',
    },
    verified: {
      label: 'Verified',
      value: official.verified ? 'Yes' : 'No',
    },
    job_status: {
      label: 'Job Status',
      value: Utils.capitalize(official.status),
    },
  }), [official, employmentTypeDisplayName]);

  // In the future, we can use this to power role based display
  const roleDisplayFieldsMap = {
    'Contract Security': {
      userDetails: ['organization', 'email', 'phone'],
      accountStatus: ['role'],
    },
    default: {
      userDetails: ['organization', 'title', 'rank', 'badge', 'email', 'phone', 'armed'],
      accountStatus: ['designation', 'role', 'verified', 'job_status'],
    },
  };

  const { userDetails, accountStatus } =
    roleDisplayFieldsMap?.[official?.role?.name] ?? roleDisplayFieldsMap.default;

  const accountStatusRows: DetailRow[] = [
    ...accountStatus.map((field) => userFields[field]),
    ...(official.pin
      ? [{ label: 'Verifier PIN', value: official.pin, testId: 'pin-row' }]
      : []),
  ];

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 6 }}>
        <DetailsSection
          title='User Details'
          rows={userDetails.map((field) => userFields[field])}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <DetailsSection title='Account & Status' rows={accountStatusRows} />
      </Grid>
    </Grid>
  );
};

export default UserDetailsView;
